/**
 * 배경색 밝기(WCAG 상대 휘도)에 맞춘 글자색.
 * 사용자 지정 배경(셀 배경색·시간표 색 등)은 다크 모드에서도 그대로 밝게 남기 때문에,
 * 밝은 배경이면 다크 모드에서도 검정 글자를 써야 읽힌다.
 */
export const CONTRAST_TEXT_ON_LIGHT = '#1a1a1a';
export const CONTRAST_TEXT_ON_DARK = '#ffffff';

function parseHexRgb(hex: string): [number, number, number] | null {
  let h = (hex ?? '').trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(h)) h = h.replace(/./g, (c) => c + c);
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [0, 2, 4].map((i) => Number.parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** WCAG 2.x 상대 휘도 (0 = 검정, 1 = 흰색). 해석 불가하면 null */
export function hexRelativeLuminance(hex: string): number | null {
  const rgb = parseHexRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

const ON_LIGHT_LUMINANCE = hexRelativeLuminance(CONTRAST_TEXT_ON_LIGHT) ?? 0;

/** 검정(#1a1a1a) 글자가 흰 글자보다 대비가 높으면 true */
export function isLightBackgroundHex(hex: string): boolean {
  const l = hexRelativeLuminance(hex);
  if (l == null) return false;
  const contrastWithWhite = 1.05 / (l + 0.05);
  const contrastWithDark = (l + 0.05) / (ON_LIGHT_LUMINANCE + 0.05);
  return contrastWithDark >= contrastWithWhite;
}

/** 배경 hex에 올릴 글자색: 밝은 배경 → #1a1a1a, 어두운 배경 → #ffffff. 해석 불가하면 fallback */
export function contrastTextColorForBackground(hex: string, fallback: string = CONTRAST_TEXT_ON_LIGHT): string {
  if (hexRelativeLuminance(hex) == null) return fallback;
  return isLightBackgroundHex(hex) ? CONTRAST_TEXT_ON_LIGHT : CONTRAST_TEXT_ON_DARK;
}
